import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { useConfirm } from "../shared/confirm-dialog";

type Tab = "plugins" | "groups" | "assignments";

interface Plugin {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  subCategory: string | null;
  platformTarget: string | null;
  latestStable: string | null;
  createdAt: string;
}

// GET /api/plugins-admin/groups renvoie les lignes brutes (snake_case).
interface Group {
  id: string;
  name: string;
  description: string | null;
  plugin_count: number;
  created_at: string;
}

interface Tenant {
  id: string;
  name: string;
}

interface Assignment {
  tenantId: string;
  pluginId: string;
  allowed: boolean;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

async function jsend(url: string, method: string, body?: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new Error((data?.error as string) || `HTTP ${r.status}`);
  return data;
}

function slugifyId(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64);
}

export function SuperAdminPluginsPage() {
  const { confirm } = useConfirm();
  const [tab, setTab] = createSignal<Tab>("plugins");
  const [err, setErr] = createSignal<string | null>(null);

  // — Création plugin —
  const [newId, setNewId] = createSignal("");
  const [newName, setNewName] = createSignal("");
  const [newCategory, setNewCategory] = createSignal("");
  const [newSub, setNewSub] = createSignal("");
  const [newPlatform, setNewPlatform] = createSignal("");

  // — Assignations tenant —
  const [selectedTenant, setSelectedTenant] = createSignal<string>("");
  const [selectedPlugins, setSelectedPlugins] = createSignal<Set<string>>(new Set());

  // — Groupes —
  const [newGroupName, setNewGroupName] = createSignal("");
  const [newGroupDesc, setNewGroupDesc] = createSignal("");
  const [managingGroupId, setManagingGroupId] = createSignal<string | null>(null);
  const [groupPluginSet, setGroupPluginSet] = createSignal<Set<string>>(new Set());

  const pluginsQ = createQuery<{ ok: boolean; plugins: Plugin[] }>(() => ({
    queryKey: ["plugins-admin-plugins"],
    queryFn: () => jget<{ ok: boolean; plugins: Plugin[] }>("/api/plugins-admin"),
  }));

  const groupsQ = createQuery<{ ok: boolean; groups: Group[] }>(() => ({
    queryKey: ["plugins-admin-groups"],
    queryFn: () => jget<{ ok: boolean; groups: Group[] }>("/api/plugins-admin/groups"),
  }));

  const tenantsQ = createQuery<{ ok: boolean; tenants: Tenant[] }>(() => ({
    queryKey: ["super-admin-tenants"],
    queryFn: () => jget<{ ok: boolean; tenants: Tenant[] }>("/api/super-admin/tenants"),
  }));

  const assignmentsQ = createQuery<{ ok: boolean; assignments: Assignment[] }>(() => ({
    queryKey: ["plugins-admin-assignments", selectedTenant()],
    queryFn: () =>
      jget<{ ok: boolean; assignments: Assignment[] }>(
        `/api/plugins-admin/tenant-plugins?tenantId=${selectedTenant()}`,
      ),
    enabled: () => !!selectedTenant(),
  }));

  // Pré-coche les plugins déjà autorisés pour le tenant sélectionné.
  // L'API tenant-plugins renvoie le produit croisé tenant×plugin avec un flag
  // `allowed` ; on ne garde que les autorisés.
  createEffect(() => {
    const data = assignmentsQ.data;
    if (!data?.assignments) return;
    const allowed = new Set(
      data.assignments.filter((a) => a.allowed && a.tenantId === selectedTenant()).map((a) => a.pluginId),
    );
    setSelectedPlugins(allowed);
  });

  async function run(fn: () => Promise<unknown>) {
    setErr(null);
    try {
      await fn();
      pluginsQ.refetch();
      groupsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  function addPlugin() {
    const name = newName().trim();
    if (!name) return;
    const id = newId().trim() || slugifyId(name);
    if (!id) {
      setErr("id_invalide");
      return;
    }
    run(() =>
      jsend("/api/plugins-admin", "POST", {
        id,
        name,
        category: newCategory().trim() || undefined,
        subCategory: newSub().trim() || undefined,
        platformTarget: newPlatform().trim() || undefined,
      }),
    ).then(() => {
      setNewId("");
      setNewName("");
      setNewCategory("");
      setNewSub("");
      setNewPlatform("");
    });
  }

  async function deletePlugin(id: string, name: string) {
    const ok = await confirm({
      level: "danger",
      title: "Supprimer ce plugin ?",
      entityName: name,
    });
    if (!ok) return;
    run(() => jsend(`/api/plugins-admin/${id}`, "DELETE"));
  }

  // — Groupes —
  function addGroup() {
    const name = newGroupName().trim();
    if (!name) return;
    run(() =>
      jsend("/api/plugins-admin/groups", "POST", {
        name,
        description: newGroupDesc().trim() || undefined,
      }),
    ).then(() => {
      setNewGroupName("");
      setNewGroupDesc("");
    });
  }

  async function deleteGroup(id: string, name: string) {
    const ok = await confirm({
      level: "danger",
      title: "Supprimer ce groupe ?",
      entityName: name,
    });
    if (!ok) return;
    if (managingGroupId() === id) setManagingGroupId(null);
    run(() => jsend(`/api/plugins-admin/groups/${id}`, "DELETE"));
  }

  async function toggleManageGroup(id: string) {
    if (managingGroupId() === id) {
      setManagingGroupId(null);
      return;
    }
    setErr(null);
    try {
      const data = await jget<{ ok: boolean; plugins: { id: string }[] }>(`/api/plugins-admin/groups/${id}/plugins`);
      setGroupPluginSet(new Set((data.plugins || []).map((p) => p.id)));
      setManagingGroupId(id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  function toggleGroupPlugin(pluginId: string) {
    const st = new Set(groupPluginSet());
    if (st.has(pluginId)) st.delete(pluginId);
    else st.add(pluginId);
    setGroupPluginSet(st);
  }

  async function saveGroupPlugins(id: string) {
    setErr(null);
    try {
      await jsend(`/api/plugins-admin/groups/${id}/plugins`, "PUT", {
        pluginIds: Array.from(groupPluginSet()),
      });
      setManagingGroupId(null);
      groupsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  // — Assignations —
  function toggleAssignment(pluginId: string) {
    const st = new Set(selectedPlugins());
    if (st.has(pluginId)) st.delete(pluginId);
    else st.add(pluginId);
    setSelectedPlugins(st);
  }

  async function saveAssignments() {
    if (!selectedTenant()) return;
    setErr(null);
    try {
      await jsend(`/api/plugins-admin/tenant-plugins/${selectedTenant()}`, "PUT", {
        pluginIds: Array.from(selectedPlugins()),
      });
      assignmentsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  const inputStyle = {
    background: "var(--bg-2, #1e293b)",
    color: "var(--text-1, #e8ecf3)",
    border: "1px solid var(--border, #334155)",
    "border-radius": "4px",
    padding: "4px 8px",
    "font-size": "12px",
  } as const;

  const tabBtn = (t: Tab) => ({
    padding: "6px 10px",
    "border-radius": "4px",
    "font-size": "12px",
    "font-weight": "500",
    cursor: "pointer",
    border: "none",
    background: tab() === t ? "var(--bg-3, #0f172a)" : "transparent",
    color: tab() === t ? "var(--text-1, #e8ecf3)" : "var(--text-4, #94a3b8)",
  });

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Plugins
          <span class="plugins-console-sub">catalogue, groupes & assignations tenant</span>
        </span>
      </div>

      <div style={{ display: "flex", gap: "6px", "margin-bottom": "10px" }}>
        <button style={tabBtn("plugins")} type="button" onClick={() => setTab("plugins")}>
          Plugins
        </button>
        <button style={tabBtn("groups")} type="button" onClick={() => setTab("groups")}>
          Groupes
        </button>
        <button style={tabBtn("assignments")} type="button" onClick={() => setTab("assignments")}>
          Assignations
        </button>
      </div>

      <Show when={err()}>
        <div
          style={{
            background: "rgba(220,38,38,0.12)",
            border: "1px solid var(--danger, #dc2626)",
            color: "var(--danger, #f87171)",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {err()}
        </div>
      </Show>

      <Show when={tab() === "plugins"}>
        <div style={{ display: "flex", gap: "6px", padding: "8px", "flex-wrap": "wrap", "align-items": "center" }}>
          <input
            style={{ ...inputStyle, width: "120px" }}
            placeholder="ID (auto si vide)"
            value={newId()}
            onInput={(e) => setNewId(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            placeholder="Nom du plugin"
            value={newName()}
            onInput={(e) => setNewName(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            placeholder="Catégorie"
            value={newCategory()}
            onInput={(e) => setNewCategory(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            placeholder="Sous-catégorie"
            value={newSub()}
            onInput={(e) => setNewSub(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            placeholder="Plateforme"
            value={newPlatform()}
            onInput={(e) => setNewPlatform(e.currentTarget.value)}
          />
          <button class="btn btn--primary btn--xs" type="button" onClick={addPlugin}>
            + Ajouter
          </button>
        </div>
        <div class="rmm-table-wrap">
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Plateforme</th>
                <th>Stable</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={!pluginsQ.isLoading}
                fallback={
                  <tr>
                    <td colSpan={5}>Chargement…</td>
                  </tr>
                }
              >
                <For each={pluginsQ.data?.plugins || []}>
                  {(p) => (
                    <tr>
                      <td style={{ color: "var(--text-1)", "font-weight": "500" }}>{p.name}</td>
                      <td style={{ "font-size": "11px" }}>
                        {p.category || "—"}
                        {p.subCategory ? ` / ${p.subCategory}` : ""}
                      </td>
                      <td style={{ "font-size": "11px" }}>{p.platformTarget || "—"}</td>
                      <td style={{ "font-size": "11px" }}>{p.latestStable || "—"}</td>
                      <td>
                        <button
                          class="btn btn--xs btn--danger"
                          type="button"
                          onClick={() => deletePlugin(p.id, p.name)}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
                <Show when={(pluginsQ.data?.plugins || []).length === 0}>
                  <tr>
                    <td colSpan={5} style={{ color: "var(--text-4)", "text-align": "center" }}>
                      Aucun plugin.
                    </td>
                  </tr>
                </Show>
              </Show>
            </tbody>
          </table>
        </div>
      </Show>

      <Show when={tab() === "groups"}>
        <div style={{ display: "flex", gap: "6px", padding: "8px", "flex-wrap": "wrap", "align-items": "center" }}>
          <input
            style={inputStyle}
            placeholder="Nom du groupe"
            value={newGroupName()}
            onInput={(e) => setNewGroupName(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            placeholder="Description (optionnel)"
            value={newGroupDesc()}
            onInput={(e) => setNewGroupDesc(e.currentTarget.value)}
          />
          <button class="btn btn--primary btn--xs" type="button" onClick={addGroup}>
            + Créer un groupe
          </button>
        </div>
        <div class="rmm-table-wrap">
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Description</th>
                <th>Plugins</th>
                <th>Créé</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={!groupsQ.isLoading}
                fallback={
                  <tr>
                    <td colSpan={5}>Chargement…</td>
                  </tr>
                }
              >
                <For each={groupsQ.data?.groups || []}>
                  {(g) => (
                    <>
                      <tr>
                        <td style={{ color: "var(--text-1)", "font-weight": "500" }}>{g.name}</td>
                        <td style={{ "font-size": "11px" }}>{g.description || "—"}</td>
                        <td style={{ "font-size": "11px" }}>{g.plugin_count} plugin(s)</td>
                        <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                          {g.created_at ? new Date(g.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td style={{ display: "flex", gap: "4px" }}>
                          <button class="btn btn--xs" type="button" onClick={() => toggleManageGroup(g.id)}>
                            {managingGroupId() === g.id ? "Fermer" : "Gérer plugins"}
                          </button>
                          <button
                            class="btn btn--xs btn--danger"
                            type="button"
                            onClick={() => deleteGroup(g.id, g.name)}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                      <Show when={managingGroupId() === g.id}>
                        <tr>
                          <td colSpan={5} style={{ background: "var(--bg-2, #1e293b)", padding: "8px" }}>
                            <div style={{ "font-size": "11px", color: "var(--text-4)", "margin-bottom": "6px" }}>
                              Plugins du groupe « {g.name} »
                            </div>
                            <div
                              style={{
                                display: "grid",
                                "grid-template-columns": "repeat(auto-fill,minmax(180px,1fr))",
                                gap: "4px",
                                "margin-bottom": "8px",
                              }}
                            >
                              <For each={pluginsQ.data?.plugins || []}>
                                {(p) => {
                                  const checked = createMemo(() => groupPluginSet().has(p.id));
                                  return (
                                    <label
                                      style={{
                                        display: "flex",
                                        "align-items": "center",
                                        gap: "6px",
                                        "font-size": "12px",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked()}
                                        onChange={() => toggleGroupPlugin(p.id)}
                                      />
                                      {p.name}
                                    </label>
                                  );
                                }}
                              </For>
                            </div>
                            <button
                              class="btn btn--primary btn--xs"
                              type="button"
                              onClick={() => saveGroupPlugins(g.id)}
                            >
                              Enregistrer
                            </button>
                          </td>
                        </tr>
                      </Show>
                    </>
                  )}
                </For>
                <Show when={(groupsQ.data?.groups || []).length === 0}>
                  <tr>
                    <td colSpan={5} style={{ color: "var(--text-4)", "text-align": "center" }}>
                      Aucun groupe.
                    </td>
                  </tr>
                </Show>
              </Show>
            </tbody>
          </table>
        </div>
      </Show>

      <Show when={tab() === "assignments"}>
        <div style={{ padding: "8px" }}>
          <select
            style={{ ...inputStyle, "margin-bottom": "10px" }}
            value={selectedTenant()}
            onChange={(e) => {
              setSelectedTenant(e.currentTarget.value);
              setSelectedPlugins(new Set<string>());
            }}
          >
            <option value="">Choisir un tenant…</option>
            <For each={tenantsQ.data?.tenants || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
          </select>
        </div>

        <Show when={selectedTenant()}>
          <div class="rmm-table-wrap">
            <table class="rmm-table">
              <thead>
                <tr>
                  <th>Plugin</th>
                  <th>Catégorie</th>
                  <th>Plateforme</th>
                  <th>Autorisé</th>
                </tr>
              </thead>
              <tbody>
                <For each={pluginsQ.data?.plugins || []}>
                  {(p) => {
                    const assigned = createMemo(() => selectedPlugins().has(p.id));
                    return (
                      <tr>
                        <td style={{ "font-weight": "500" }}>{p.name}</td>
                        <td style={{ "font-size": "11px" }}>{p.category || "—"}</td>
                        <td style={{ "font-size": "11px" }}>{p.platformTarget || "—"}</td>
                        <td>
                          <input type="checkbox" checked={assigned()} onChange={() => toggleAssignment(p.id)} />
                        </td>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
            <div style={{ padding: "8px" }}>
              <button class="btn btn--primary btn--xs" type="button" onClick={saveAssignments}>
                Sauvegarder assignations
              </button>
            </div>
          </div>
        </Show>
      </Show>
    </div>
  );
}
